'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { type ConnectUseCase, findConnectUseCase, USE_CASE_PARAM } from '../use-cases';

/**
 * Captures a `?usecase=` arrival from a Connect channel on the /dani landing
 * page, then strips the param so a refresh or back never refills the box.
 * An unknown id is ignored.
 *
 * Same shape as `useDaniDeepLinkPrompt`: the use case is captured once into
 * state BEFORE the strip, and `enabled` must be the session-ready signal so
 * the param survives the sign-in redirect for a signed-out visitor.
 */
export function useConnectUseCaseParam(enabled: boolean): ConnectUseCase | null {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const router = useRouter();

    const [captured] = useState(() => findConnectUseCase(searchParams?.get(USE_CASE_PARAM)));
    const strippedRef = useRef(false);

    useEffect(() => {
        if (!enabled || strippedRef.current || !searchParams?.has(USE_CASE_PARAM)) return;
        strippedRef.current = true;
        const rest = new URLSearchParams(searchParams.toString());
        rest.delete(USE_CASE_PARAM);
        const query = rest.toString();
        router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    }, [enabled, pathname, router, searchParams]);

    return enabled ? captured : null;
}
